import React, { useState, useEffect, useContext, useMemo } from "react";
import { Link, useMatch, useNavigate } from "react-router-dom";
import profile_placeholder from "../assets/images/profile_placeholder.png";
import henceforthApi from "../utils/henceforthApi";
import { GlobalContext } from "../context/Provider";
import Swal from "sweetalert2";
import moment from "moment";
import { toast } from "react-toastify";
import RideCreated from "./rideCreated";
import RideBooked from "./rideBooked";
import UserVehicles from "./userVehicles";
import WalletTransation from "./walletTransation";
import AgGridTable from "../components/ui/AgGridTable";
import { ColDef } from "ag-grid-community";
import {
  AlertCircle,
  Calendar,
  Car,
  Check,
  CheckCircle2,
  Clock,
  Compass,
  Copy,
  ExternalLink,
  Eye,
  FileBadge,
  FileText,
  Globe,
  IdCard,
  Info,
  Loader2,
  Lock,
  Mail,
  PauseCircle,
  Plus,
  Receipt,
  RefreshCw,
  Repeat,
  ShieldCheck,
  Star,
  Trash2,
  TrendingUp,
  Unlock,
  User,
  Users,
  Wallet,
  X,
  XCircle,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { formatPhone } from "../utils/phone";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from "../components/ui/dialog";

type UserDetailsType = {
  account_status: string;
  account_type: string;
  admin_verified: boolean;
  age_verified: boolean;
  bio: string;
  image: string;
  cover_img: string;
  created_at: string;
  email: string;
  email_verified: boolean;
  name: string;
  is_blocked: boolean;
  is_deleted: boolean;
  last_name: string;
  last_seen?: null;
  profile_pic: string;
  social_token: string;
  licence_id: string;
  phone_no: string | number;
  country_code?: string;
  gender: string;
  front_doc: any;
  document_status: string;
  passenger_document?: string;
  passenger_selfie?: string;
  passenger_document_status?: string;
  passenger_document_verified?: boolean;
  vehicle_licence?: string;
  driving_licence?: string;
  driver_document_status?: string;
  driver_document_verified?: boolean;
  driver_document_rejection_reason?: string;
  driver_document_reject_reason?: string;
  rejection_reason?: string;
  reject_reason?: string;
  social_type: string;
  total_coins: number;
  updated_at: null;
  date_of_birth: string;
  user_name: string;
  user_status: number;
  _id: string;
  stripeAccountId: string;
  referral_code?: string;
  referral_count?: number;
  country?: string;
  state?: string;
  state_name?: string;
  is_referral_coupon_credit_disable?: boolean | null;
  wallet: {
    balance: number;
    total_earned: number;
    total_withdrawn: number;
  };
};

export default () => {
  const match: any = useMatch("/user/:id");
  const navigate = useNavigate();
  const { authState, handleError } = useContext(GlobalContext);

  if (authState.access_token) {
    henceforthApi.setToken(authState.access_token);
  }

  const [userDetails, setUserDetails] = useState<UserDetailsType>({
    account_status: "",
    account_type: "",
    admin_verified: false,
    age_verified: false,
    bio: "",
    image: "",
    cover_img: "",
    created_at: "",
    gender: "",
    email: "",
    phone_no: "",
    document_status: "",
    licence_id: "",
    email_verified: false,
    name: "",
    is_blocked: false,
    is_deleted: false,
    last_name: "",
    last_seen: null,
    front_doc: "",
    passenger_document: "",
    passenger_selfie: "",
    passenger_document_status: "",
    passenger_document_verified: false,
    vehicle_licence: "",
    driving_licence: "",
    driver_document_status: "",
    driver_document_verified: false,
    date_of_birth: "",
    profile_pic: "",
    social_token: "",
    social_type: "",
    total_coins: 0,
    updated_at: null,
    user_name: "",
    user_status: 0,
    _id: "",
    stripeAccountId: "",
    referral_code: "",
    referral_count: 0,
    country: "",
    state: "",
    state_name: "",
    is_referral_coupon_credit_disable: null,
    wallet: {
      balance: 0,
      total_earned: 0,
      total_withdrawn: 0,
    },
  });

  const [referralUsers, setReferralUsers] = useState<Array<any>>([]);
  const [referralLoading, setReferralLoading] = useState(false);
  const [updatingReferral, setUpdatingReferral] = useState(false);
  const [activeTab, setActiveTab] = useState<"booked" | "created" | "vehicles" | "wallet" | "referral">("booked");
  const [previewDoc, setPreviewDoc] = useState<string | null>(null);
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [rejectingDocType, setRejectingDocType] = useState("User Documents");
  const [rejectionReason, setRejectionReason] = useState("");
  const [rejectSubmitting, setRejectSubmitting] = useState(false);

  const getUserDetails = async () => {
    try {
      const res = await henceforthApi.User.getUserDetails(match.params.id);
      setUserDetails(res.data);
    } catch (error) {
      handleError(error);
    }
  };

  const getReferralUsers = async () => {
    setReferralLoading(true);
    try {
      const res = await henceforthApi.User.getReferralUsers(match.params.id);
      setReferralUsers(res.data?.data || []);
    } catch (error) {
      handleError(error);
      setReferralUsers([]);
    } finally {
      setReferralLoading(false);
    }
  };

  useEffect(() => {
    if (match?.params?.id) {
      getUserDetails();
      getReferralUsers();
    }
  }, [match?.params?.id]);

  const copyToClipboard = (text: string, label = "Copied to clipboard") => {
    if (!text) return;
    navigator?.clipboard?.writeText(text);
    toast.success(label);
  };

  // Old codebase helper methods
  const getDocumentStatus = (status?: string): string => {
    if (!status) return "NOT_VERIFY";
    return status.toUpperCase();
  };

  const formatDisplayStatus = (status?: string): string => {
    if (!status || status === "NOT_VERIFY" || status === "NOT_VERIFIED") return "NOT VERIFY";
    return status.toUpperCase().replace(/_/g, " ");
  };

  const hasVehicleLicence = Boolean(
    userDetails?.vehicle_licence &&
    userDetails?.vehicle_licence !== null &&
    userDetails?.vehicle_licence !== "null"
  );

  const hasDrivingLicence = Boolean(
    userDetails?.driving_licence &&
    userDetails?.driving_licence !== null &&
    userDetails?.driving_licence !== "null"
  );

  const hasDriverDocuments = () => hasVehicleLicence || hasDrivingLicence;

  const hasPendingDriverDocuments = () => {
    const driverStatus = getDocumentStatus(userDetails?.driver_document_status);
    return driverStatus === "PENDING";
  };

  const handleExpireReferral = async () => {
    if (updatingReferral) return;
    Swal.fire({
      title: "Expire Referral?",
      text: "This will disable the referral coupon credit for this user.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, Expire it!",
    }).then(async (result: any) => {
      if (result.isConfirmed) {
        try {
          setUpdatingReferral(true);
          await henceforthApi.User.updateReferralCouponSettings({
            is_referral_coupon_credit_disable: true,
            user_id: userDetails._id,
          });
          setUserDetails((prev) => ({
            ...prev,
            is_referral_coupon_credit_disable: true,
          }));
          Swal.fire({
            title: "Success!",
            text: "Referral coupon has been expired successfully",
            icon: "success",
            confirmButtonText: "OK",
          });
          getUserDetails();
        } catch (error: any) {
          console.log("Expire referral failed", error);
          Swal.fire({
            title: "Error!",
            text: error.response?.body?.error_description || "Failed to expire referral coupon",
            icon: "error",
            confirmButtonText: "Cancel",
          });
        } finally {
          setUpdatingReferral(false);
        }
      }
    });
  };

  const handleRenewReferral = async () => {
    if (updatingReferral) return;
    Swal.fire({
      title: "Renew Referral?",
      text: "This will enable the referral coupon credit for this user.",
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, Renew it!",
    }).then(async (result: any) => {
      if (result.isConfirmed) {
        try {
          setUpdatingReferral(true);
          await henceforthApi.User.updateReferralCouponSettings({
            is_referral_coupon_credit_disable: false,
            user_id: userDetails._id,
          });
          setUserDetails((prev) => ({
            ...prev,
            is_referral_coupon_credit_disable: false,
          }));
          Swal.fire({
            title: "Success!",
            text: "Referral coupon has been renewed successfully",
            icon: "success",
            confirmButtonText: "OK",
          });
          getUserDetails();
        } catch (error: any) {
          console.log("Renew referral failed", error);
          Swal.fire({
            title: "Error!",
            text: error.response?.body?.error_description || "Failed to renew referral coupon",
            icon: "error",
            confirmButtonText: "Cancel",
          });
        } finally {
          setUpdatingReferral(false);
        }
      }
    });
  };

  // Asks the admin for a mandatory reason; returns null if cancelled.
  const askActionReason = async (title: string, confirmText: string) => {
    const result = await Swal.fire({
      title,
      input: "textarea",
      inputLabel: "Reason (this will be shared with the user)",
      inputPlaceholder: "Type the reason here...",
      inputAttributes: { maxlength: "300" },
      showCancelButton: true,
      confirmButtonText: confirmText,
      customClass: {
        popup: "swal-reason-popup",
        confirmButton: "swal-reason-confirm",
      },
      inputValidator: (value) =>
        !value?.trim() ? "Please enter a reason" : null,
    });
    return result.isConfirmed ? String(result.value).trim() : null;
  };

  const handleBlocking = async () => {
    let reason: string | null = null;
    if (!userDetails.is_blocked) {
      reason = await askActionReason("Block this user?", "Block");
      if (reason === null) return;
    }
    const data = {
      type: "BLOCK/DELETE",
      _id: userDetails._id,
      is_blocked: !Boolean(userDetails.is_blocked),
      is_deleted: userDetails.is_deleted,
      language: "ENGLISH",
      account_status: userDetails.account_status,
      reason: reason || undefined,
    } as any;
    try {
      await henceforthApi.User.manageUsers(data);
      {
        !userDetails.is_blocked
          ? Swal.fire({
            title: "User Blocked",
            text: "User Blocked  successfully",
            icon: "success",
            confirmButtonText: "OK",
          })
          : Swal.fire({
            title: "User Unblocked",
            text: "User Unblocked  successfully",
            icon: "success",
            confirmButtonText: "OK",
          });
      }
      getUserDetails();
    } catch (error: any) {
      console.log(error, "error is ");
      Swal.fire({
        title: "Error!",
        text: error?.response?.body?.error_description || "Action failed",
        icon: "error",
        confirmButtonText: "Cancel",
      });
    }
  };

  const handleActivate = async () => {
    let reason: string | null = null;
    if (userDetails.account_status == "ACTIVATED") {
      reason = await askActionReason("Deactivate this account?", "Deactivate");
      if (reason === null) return;
    }
    const data = {
      type: "ACTIVATE/DEACTIVATE",
      _id: userDetails._id,
      is_blocked: userDetails.is_blocked,
      is_deleted: userDetails.is_deleted,
      language: "ENGLISH",
      account_status:
        userDetails.account_status == "ACTIVATED" ? `DEACTIVATED` : `ACTIVATED`,
      reason: reason || undefined,
    } as any;

    try {
      await henceforthApi.User.manageUsers(data);
      {
        userDetails.account_status == "DEACTIVATED"
          ? Swal.fire({
            title: "Activated",
            text: "Account Activated Successfully",
            icon: "success",
            confirmButtonText: "OK",
          })
          : Swal.fire({
            title: "Deactivated",
            text: "Account Deactivated Successfully",
            icon: "success",
            confirmButtonText: "OK",
          });
      }
      getUserDetails();
    } catch (error: any) {
      console.log(error, "error is ");
    }
  };

  const handleDelete = async () => {
    const data = {
      type: "BLOCK/DELETE",
      _id: userDetails._id,
      is_blocked: userDetails.is_blocked,
      is_deleted: true,
      language: "ENGLISH",
      account_status: userDetails.account_status,
    };

    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    }).then(async (result: any) => {
      if (result.isConfirmed) {
        try {
          henceforthApi.User.manageUsers(data)
            .then((res: any) => {
              Swal.fire({
                title: "User Deleted",
                text: res.data.message,
                icon: "success",
                confirmButtonText: "OK",
              });
              window.history.back();
            })
            .catch((err: any) => {
              Swal.fire({
                title: "Error!",
                text: err?.response?.body?.error_description || err,
                icon: "error",
                confirmButtonText: "Cancel",
              });
            });
        } catch (error: any) { }
      }
    });
  };

  const verifyDocument = async (documentType = "User Documents") => {
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, Verify it!",
    }).then(async (result: any) => {
      if (result.isConfirmed) {
        try {
          const apiCall =
            documentType === "Passage Documents"
              ? henceforthApi.User.verifyPassengerDocuments(match?.params.id, 1)
              : henceforthApi.User.verifyDriverDocuments(match?.params.id, 1);

          await apiCall
            .then((res: any) => {
              Swal.fire({
                title: "Verification Successful",
                text: `${documentType} verified successfully`,
                icon: "success",
                confirmButtonText: "OK",
              });
              getUserDetails();
            })
            .catch((err: any) => {
              console.log("Verify failed", err);
              Swal.fire({
                title: "Verified Failed",
                text: err.response?.body?.error_description || "Failed to verify document",
                icon: "error",
                confirmButtonText: "Cancel",
              });
            });
        } catch { }
      }
    });
  };

  const openRejectModal = (documentType = "User Documents") => {
    setRejectingDocType(documentType);
    setRejectionReason("");
    setRejectModalOpen(true);
  };

  const rejectDocument = (documentType = "User Documents") => {
    openRejectModal(documentType);
  };

  const handleConfirmReject = async () => {
    if (!rejectionReason.trim()) {
      toast.error("Please enter a rejection reason");
      return;
    }
    setRejectSubmitting(true);
    try {
      const trimmedReason = rejectionReason.trim();
      const apiCall =
        rejectingDocType === "Passage Documents"
          ? henceforthApi.User.verifyPassengerDocuments(match?.params.id, 0, trimmedReason)
          : henceforthApi.User.verifyDriverDocuments(match?.params.id, 0, trimmedReason);

      await apiCall;
      setRejectModalOpen(false);
      setRejectionReason("");
      Swal.fire({
        title: "Document Rejected",
        text: `${rejectingDocType} has been rejected. The user has been notified of the reason.`,
        icon: "success",
        confirmButtonText: "OK",
      });
      getUserDetails();
    } catch (err: any) {
      console.log("Reject failed", err);
      Swal.fire({
        title: "Reject Failed",
        text: err.response?.body?.error_description || err.response?.body?.message || "Failed to reject document",
        icon: "error",
        confirmButtonText: "OK",
      });
    } finally {
      setRejectSubmitting(false);
    }
  };

  const initials = useMemo(() => {
    if (!userDetails?.name) return "FT";
    const parts = userDetails.name.trim().split(" ");
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return userDetails.name.slice(0, 2).toUpperCase();
  }, [userDetails?.name]);

  const shortUid = userDetails?._id ? userDetails._id.slice(0, 8) : "6acf4bba";
  const isDocumentVerified = userDetails?.driver_document_verified || userDetails?.admin_verified;
  const isUserActive = userDetails?.account_status === "ACTIVATED" && !userDetails?.is_blocked;

  const referralColumnDefs = useMemo<ColDef[]>(() => {
    return [
      {
        headerName: "Sr No.",
        minWidth: 70,
        maxWidth: 80,
        width: 75,
        valueGetter: (p) => (p.node?.rowIndex ?? 0) + 1,
        cellClass: "font-medium text-slate-500",
      },
      {
        headerName: "Name",
        field: "name",
        minWidth: 150,
        flex: 1,
        cellRenderer: (p: any) => {
          return (
            <span className="font-semibold text-slate-800">
              {p.value || "Not Available"}
            </span>
          );
        },
      },
      {
        headerName: "Email",
        field: "email",
        minWidth: 180,
        flex: 1.2,
        valueFormatter: (p) => p.value || "Not Available",
      },
      {
        headerName: "Phone",
        field: "phone_no",
        minWidth: 130,
        width: 140,
        valueFormatter: (p) => formatPhone(p.value, p.data?.country_code),
      },
      {
        headerName: "Joined",
        field: "created_at",
        minWidth: 120,
        width: 130,
        valueFormatter: (p) =>
          p.value ? moment(Number(p.value)).format("DD MMM YYYY") : "N/A",
      },
      {
        headerName: "Status",
        field: "status",
        minWidth: 100,
        width: 110,
        cellRenderer: (p: any) => {
          return (
            <span className="inline-flex px-2 py-0.5 rounded text-[11px] font-semibold bg-emerald-50 text-emerald-700">
              {p.value || "Active"}
            </span>
          );
        },
      },
    ];
  }, []);

  return (
    <div className="space-y-4 pb-12">
      {/* Top Breadcrumb & Actions Row */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
        <div>
          <div className="flex items-center gap-1.5 text-xs text-slate-400 font-medium">
            <Link to="/" className="text-slate-500 hover:text-emerald-700">
              Dashboard
            </Link>
            <span>›</span>
            <Link to="/users/1" className="text-slate-500 hover:text-emerald-700">
              Users
            </Link>
            <span>›</span>
            <span className="text-emerald-700 font-semibold">User Details</span>
          </div>

          <h1 className="text-xl sm:text-2xl font-bold text-slate-900 tracking-tight mt-1">
            User Details
          </h1>
          <p className="text-xs text-slate-500 mt-0.5">
            Overview, operational controls, and verification pipeline for{" "}
            <span className="font-semibold text-slate-700">
              {userDetails?.email || "first01@yopmail.com"}
            </span>
          </p>
        </div>

        {/* Top-Right Operation Buttons: Block / Deactivate / Delete */}
        <div className="flex items-center gap-2 self-start md:self-center">
          <Button
            type="button"
            onClick={handleBlocking}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-amber-50 text-amber-700 hover:bg-amber-100 border border-amber-200/80 transition-colors cursor-pointer"
          >
            <i className={userDetails?.is_blocked == false ? `fas fa-unlock me-1` : `fas fa-lock me-1`}></i>
            <span>{userDetails?.is_blocked == false ? "Block" : "Unblock"}</span>
          </Button>

          <Button
            type="button"
            onClick={handleActivate}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-white text-slate-700 hover:bg-slate-50 border border-slate-200 transition-colors cursor-pointer shadow-2xs"
          >
            <i className={userDetails?.account_status == "ACTIVATED" ? `fas fa-unlock me-1` : `fas fa-lock me-1`}></i>
            <span>{userDetails?.account_status == "ACTIVATED" ? "Deactivate" : "Activate"}</span>
          </Button>

          {/* <Button
            type="button"
            onClick={handleDelete}
            disabled={userDetails?.is_deleted ? true : false}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-rose-50 text-rose-700 hover:bg-rose-100 border border-rose-200/80 transition-colors cursor-pointer disabled:opacity-50"
          >
            <i className={userDetails?.is_deleted ? `fa fa-ban me-1` : "fas fa-trash-alt me-1"}></i>
            <span>{userDetails?.is_deleted ? "Deleted" : "Delete"}</span>
          </Button> */}
        </div>
      </div>

      {/* User Header Profile Card */}
      <div className="bg-white rounded-2xl border border-slate-200/80 p-5 shadow-2xs">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-5">
          {/* Left: Avatar + Details + Badges */}
          <div className="flex items-center gap-4">
            <div className="relative">
              {userDetails?.profile_pic && userDetails?.profile_pic !== "null" ? (
                <img
                  src={henceforthApi.FILES.imageOriginal(userDetails.profile_pic)}
                  alt={userDetails.name}
                  className="w-16 h-16 rounded-full object-cover border-2 border-emerald-500/30"
                  onError={(e) => {
                    (e.target as HTMLImageElement).src = profile_placeholder;
                  }}
                />
              ) : (
                <div className="w-16 h-16 rounded-full bg-[#dcfce7] text-[#0f5940] flex items-center justify-center font-bold text-xl select-none shadow-2xs ring-2 ring-emerald-100">
                  {initials}
                </div>
              )}
              {isUserActive && (
                <span className="w-3.5 h-3.5 rounded-full bg-emerald-500 ring-2 ring-white absolute bottom-0 right-0"></span>
              )}
            </div>

            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <h2 className="text-lg font-bold text-slate-900 leading-tight">
                  {userDetails?.name || "First Test"}
                </h2>
                {isDocumentVerified ? (
                  <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-100/60">
                    <CheckCircle2 className="w-3 h-3 text-emerald-600" />
                    Verified
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-semibold bg-slate-100 text-slate-600">
                    <Info className="w-3 h-3 text-slate-400" />
                    Not verified
                  </span>
                )}
              </div>

              <div className="flex items-center gap-1.5 text-xs text-slate-500 mt-1">
                <Mail className="w-3.5 h-3.5 text-slate-400" />
                <span>{userDetails?.email || "first01@yopmail.com"}</span>
              </div>

              {/* Meta inline row */}
              <div className="flex items-center gap-3 text-xs text-slate-500 font-medium mt-2 flex-wrap">
                <span className="flex items-center gap-1">
                  <span>♂</span>
                  <span>{userDetails?.gender ? userDetails.gender : "Not Available"}</span>
                </span>
                <span className="text-slate-300">•</span>
                <span className="flex items-center gap-1">
                  <Globe className="w-3.5 h-3.5 text-slate-400" />
                  <span>
                    {userDetails?.country ? `${userDetails.country}` : "India"}
                  </span>
                </span>
                <span className="text-slate-300">•</span>
                <span className="flex items-center gap-1">
                  <Calendar className="w-3.5 h-3.5 text-slate-400" />
                  <span>
                    Joined{" "}
                    {userDetails?.created_at
                      ? moment(Number(userDetails.created_at)).format("DD MMM YYYY")
                      : ""}
                  </span>
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Middle 2-Column Grid: User Details (Left) + Wallet & Documents (Right) */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
        {/* Left Column: User Details Credentials (7 cols) */}
        <div className="lg:col-span-7 bg-white rounded-2xl border border-slate-200/80 p-5 shadow-2xs flex flex-col justify-between">
          <div>
            <div className="flex items-center justify-between pb-3 mb-2 border-b border-slate-100">
              <div className="flex items-center gap-2">
                <IdCard className="w-4 h-4 text-emerald-700" />
                <h3 className="text-sm font-bold text-slate-900">User Details</h3>
              </div>
            </div>

            <div className="space-y-3 text-xs">
              <div className="flex items-center justify-between py-1">
                <span className="text-slate-500">Phone</span>
                <span className="font-semibold text-slate-800">
                  {formatPhone(userDetails?.phone_no, userDetails?.country_code)}
                </span>
              </div>

              <div className="flex items-center justify-between py-1 border-t border-slate-50">
                <span className="text-slate-500">Gender</span>
                <span className="font-semibold text-slate-800 uppercase">
                  {userDetails?.gender || "Not Available"}
                </span>
              </div>

              <div className="flex items-center justify-between py-1 border-t border-slate-50">
                <span className="text-slate-500">Date of Birth</span>
                <span className="font-semibold text-slate-800">
                  {userDetails?.date_of_birth
                    ? moment(userDetails.date_of_birth).isValid()
                      ? moment(userDetails.date_of_birth).format("DD MMM YYYY")
                      : userDetails.date_of_birth.slice(0, 10)
                    : "-"}
                </span>
              </div>


              <div className="flex items-center justify-between py-1 border-t border-slate-50">
                <span className="text-slate-500">Date Activate</span>
                <span className="font-semibold text-slate-800">
                  {userDetails?.created_at
                    ? moment(Number(userDetails.created_at)).format("DD MMM YYYY")
                    : "-"}
                </span>
              </div>

              <div className="flex items-center justify-between py-1 border-t border-slate-50">
                <span className="text-slate-500">Bank account Id</span>
                <span className="text-slate-400 italic">
                  {userDetails?.stripeAccountId || "Not available"}
                </span>
              </div>

              <div className="flex items-center justify-between py-1 border-t border-slate-50">
                <span className="text-slate-500">Referral Count</span>
                <span className="font-semibold text-slate-800">
                  {userDetails?.referral_count !== undefined && userDetails?.referral_count !== null
                    ? `${userDetails?.referral_count}`
                    : "0"}{" "}
                  users
                </span>
              </div>

              <div className="flex items-center justify-between py-1 border-t border-slate-50">
                <span className="text-slate-500">Referral Code</span>
                <div className="flex items-center gap-2">
                  <span className="font-mono font-bold text-xs bg-slate-50 border border-slate-200 px-2 py-0.5 rounded text-slate-800">
                    {userDetails?.referral_code || "FM0HHJZ5"}
                  </span>
                  <Button
                    type="button"
                    onClick={() =>
                      copyToClipboard(userDetails?.referral_code || "", "Referral Code copied")
                    }
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6 text-slate-400 hover:text-slate-700 cursor-pointer"
                    title="Copy Referral Code"
                  >
                    <Copy className="w-3.5 h-3.5" />
                  </Button>

                  {userDetails?.is_referral_coupon_credit_disable === true && (
                    <Button
                      type="button"
                      onClick={handleRenewReferral}
                      disabled={updatingReferral}
                      className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-md text-[11px] font-semibold bg-emerald-50 text-emerald-700 hover:bg-emerald-100 border border-emerald-200 cursor-pointer"
                    >
                      <Repeat className="w-3 h-3" />
                      <span>Renew Referral</span>
                    </Button>
                  )}

                  {!userDetails?.is_referral_coupon_credit_disable && (
                    <Button
                      type="button"
                      onClick={handleExpireReferral}
                      disabled={updatingReferral}
                      className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-md text-[11px] font-semibold bg-rose-50 text-rose-700 hover:bg-rose-100 border border-rose-200 cursor-pointer"
                    >
                      <XCircle className="w-3 h-3" />
                      <span>Expire Referral</span>
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right Column: Wallet Cards + User Documents (5 cols) */}
        <div className="lg:col-span-5 space-y-4">
          {/* 3 Wallet Metric Cards */}
          <div className="grid grid-cols-3 gap-3">
            {/* Balance Card */}
            <div className="bg-white rounded-2xl border border-slate-200/80 p-3.5 shadow-2xs">
              <div className="flex items-center justify-between text-xs font-semibold text-slate-500">
                <span className="text-[10px] uppercase font-bold tracking-wider">
                  Balance
                </span>
                <Wallet className="w-3.5 h-3.5 text-emerald-600" />
              </div>
              <div className="text-xl font-extrabold text-slate-900 tracking-tight mt-1.5 leading-none">
                ₹{userDetails?.wallet?.balance ?? 0}{" "}
                <span className="text-xs font-normal text-slate-400">INR</span>
              </div>
              <div className="text-[10px] text-slate-400 mt-1 leading-tight">
                Available in wallet
              </div>
            </div>

            {/* Earned Card */}
            <div className="bg-white rounded-2xl border border-slate-200/80 p-3.5 shadow-2xs">
              <div className="flex items-center justify-between text-xs font-semibold text-slate-500">
                <span className="text-[10px] uppercase font-bold tracking-wider">
                  Earned
                </span>
                <TrendingUp className="w-3.5 h-3.5 text-blue-600" />
              </div>
              <div className="text-xl font-extrabold text-slate-900 tracking-tight mt-1.5 leading-none">
                ₹{userDetails?.wallet?.total_earned ?? 0}{" "}
                <span className="text-xs font-normal text-slate-400">INR</span>
              </div>
              <div className="text-[10px] text-slate-400 mt-1 leading-tight">
                Lifetime commissions
              </div>
            </div>

            {/* Withdrawn Card */}
            <div className="bg-white rounded-2xl border border-slate-200/80 p-3.5 shadow-2xs">
              <div className="flex items-center justify-between text-xs font-semibold text-slate-500">
                <span className="text-[10px] uppercase font-bold tracking-wider">
                  Withdrawn
                </span>
                <Receipt className="w-3.5 h-3.5 text-slate-500" />
              </div>
              <div className="text-xl font-extrabold text-slate-900 tracking-tight mt-1.5 leading-none">
                ₹{userDetails?.wallet?.total_withdrawn ?? 0}{" "}
                <span className="text-xs font-normal text-slate-400">INR</span>
              </div>
              <div className="text-[10px] text-slate-400 mt-1 leading-tight">
                Total payouts cleared
              </div>
            </div>
          </div>

          {/* User Documents Card */}
          <div className="bg-white rounded-2xl border border-slate-200/80 p-4 shadow-2xs">
            <div className="flex items-center justify-between pb-3 mb-3 border-b border-slate-100">
              <div className="flex items-center gap-2">
                <FileBadge className="w-4 h-4 text-emerald-700" />
                <h3 className="text-sm font-bold text-slate-900">User Documents</h3>
              </div>
              <span
                className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${getDocumentStatus(userDetails?.driver_document_status) === "ACCEPTED"
                  ? "bg-emerald-50 text-emerald-700"
                  : getDocumentStatus(userDetails?.driver_document_status) === "REJECTED"
                    ? "bg-rose-50 text-rose-700"
                    : getDocumentStatus(userDetails?.driver_document_status) === "PENDING"
                      ? "bg-amber-50 text-amber-700"
                      : "bg-slate-100 text-slate-600"
                  }`}
              >
                {formatDisplayStatus(userDetails?.driver_document_status)}
              </span>
            </div>

            {/* 2 Documents Side-by-Side Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-2">
              {/* Doc 1: Vehicle Registration */}
              <div className="bg-slate-50/80 border border-slate-200/70 rounded-xl p-3 flex flex-col justify-between">
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <div className="w-7 h-7 rounded-lg bg-emerald-100/70 text-emerald-800 flex items-center justify-center">
                      <Car className="w-3.5 h-3.5" />
                    </div>
                    {/* <span
                      className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${hasVehicleLicence
                        ? hasPendingDriverDocuments()
                          ? "text-amber-700 bg-amber-50"
                          : "text-emerald-700 bg-emerald-50"
                        : "text-slate-500 bg-slate-100"
                        }`}
                    >
                      {hasVehicleLicence
                        ? hasPendingDriverDocuments()
                          ? "Pending Review"
                          : formatDisplayStatus(userDetails?.driver_document_status)
                        : "Not Uploaded"}
                    </span> */}
                  </div>
                  <div className="text-xs font-bold text-slate-800">
                    Vehicle Registration
                  </div>

                  {/* Document Preview Thumbnail */}
                  {hasVehicleLicence && userDetails.vehicle_licence ? (
                    <div
                      onClick={() => setPreviewDoc(userDetails.vehicle_licence || null)}
                      className="group relative w-full h-28 mt-2 rounded-lg overflow-hidden border border-slate-200 bg-white cursor-pointer shadow-2xs"
                      title="Click to preview Vehicle Registration"
                    >
                      <img
                        src={henceforthApi.FILES.imageOriginal(userDetails.vehicle_licence)}
                        alt="Vehicle Registration Preview"
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.style.display = "none";
                          if (target.parentElement) {
                            target.parentElement.classList.add("flex", "items-center", "justify-center");
                            target.parentElement.innerHTML = '<span class="text-[11px] text-slate-400">Preview unavailable</span>';
                          }
                        }}
                      />
                      <div className="absolute inset-0 bg-slate-900/0 group-hover:bg-slate-900/30 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
                        <span className="bg-white/95 backdrop-blur-xs text-slate-800 text-[10px] font-semibold px-2.5 py-1 rounded-md shadow-xs flex items-center gap-1.5">
                          <Eye className="w-3.5 h-3.5 text-emerald-700" />
                          <span>Preview</span>
                        </span>
                      </div>
                    </div>
                  ) : (
                    <div className="w-full h-28 mt-2 rounded-lg border border-dashed border-slate-200 bg-slate-50/70 flex flex-col items-center justify-center text-slate-400 gap-1.5 text-[11px]">
                      <FileText className="w-5 h-5 text-slate-300" />
                      <span>Not available</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Doc 2: Driving Licence */}
              <div className="bg-slate-50/80 border border-slate-200/70 rounded-xl p-3 flex flex-col justify-between">
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <div className="w-7 h-7 rounded-lg bg-emerald-100/70 text-emerald-800 flex items-center justify-center">
                      <IdCard className="w-3.5 h-3.5" />
                    </div>
                    {/* <span
                      className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${hasDrivingLicence
                        ? hasPendingDriverDocuments()
                          ? "text-amber-700 bg-amber-50"
                          : "text-emerald-700 bg-emerald-50"
                        : "text-slate-500 bg-slate-100"
                        }`}
                    >
                      {hasDrivingLicence
                        ? hasPendingDriverDocuments()
                          ? "Pending Review"
                          : formatDisplayStatus(userDetails?.driver_document_status)
                        : "Not Uploaded"}
                    </span> */}
                  </div>
                  <div className="text-xs font-bold text-slate-800">
                    Driving Licence
                  </div>

                  {/* Document Preview Thumbnail */}
                  {hasDrivingLicence && userDetails.driving_licence ? (
                    <div
                      onClick={() => setPreviewDoc(userDetails.driving_licence || null)}
                      className="group relative w-full h-28 mt-2 rounded-lg overflow-hidden border border-slate-200 bg-white cursor-pointer shadow-2xs"
                      title="Click to preview Driving Licence"
                    >
                      <img
                        src={henceforthApi.FILES.imageOriginal(userDetails.driving_licence)}
                        alt="Driving Licence Preview"
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.style.display = "none";
                          if (target.parentElement) {
                            target.parentElement.classList.add("flex", "items-center", "justify-center");
                            target.parentElement.innerHTML = '<span class="text-[11px] text-slate-400">Preview unavailable</span>';
                          }
                        }}
                      />
                      <div className="absolute inset-0 bg-slate-900/0 group-hover:bg-slate-900/30 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
                        <span className="bg-white/95 backdrop-blur-xs text-slate-800 text-[10px] font-semibold px-2.5 py-1 rounded-md shadow-xs flex items-center gap-1.5">
                          <Eye className="w-3.5 h-3.5 text-emerald-700" />
                          <span>Preview</span>
                        </span>
                      </div>
                    </div>
                  ) : (
                    <div className="w-full h-28 mt-2 rounded-lg border border-dashed border-slate-200 bg-slate-50/70 flex flex-col items-center justify-center text-slate-400 gap-1.5 text-[11px]">
                      <FileText className="w-5 h-5 text-slate-300" />
                      <span>Not available</span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Show Rejection Reason if status is REJECTED */}
            {getDocumentStatus(userDetails?.driver_document_status) === "REJECTED" && (
              <div className="mt-2.5 p-3 rounded-xl bg-rose-50/90 border border-rose-200/80 text-xs">
                <div className="flex items-center gap-1.5 text-rose-800 font-bold mb-1">
                  <AlertCircle className="w-4 h-4 text-rose-600 shrink-0" />
                  <span>Rejection Reason</span>
                </div>
                <p className="text-rose-700 text-[11px] leading-relaxed pl-5">
                  {userDetails.rejection_reason ||
                    userDetails.driver_document_rejection_reason ||
                    userDetails.driver_document_reject_reason ||
                    userDetails.reject_reason ||
                    "Documents were rejected. User must re-upload valid documents."}
                </p>
              </div>
            )}

            {/* Document Action Buttons - Only rendered when hasPendingDriverDocuments() as in old codebase */}
            {hasPendingDriverDocuments() && (
              <div className="flex items-center justify-end gap-2.5 pt-2 border-t border-slate-100">
                <Button
                  type="button"
                  onClick={() => openRejectModal("User Documents")}
                  className="py-1.5 px-3.5 rounded-lg text-xs font-semibold bg-rose-50 hover:bg-rose-100 text-rose-700 border border-rose-200 flex items-center justify-center gap-1.5 transition-colors cursor-pointer"
                >
                  <X className="w-3.5 h-3.5" />
                  <span>Reject</span>
                </Button>

                <Button
                  type="button"
                  onClick={() => verifyDocument("User Documents")}
                  className="py-1.5 px-3.5 rounded-lg text-xs font-semibold bg-[#004d34] hover:bg-[#003826] text-white flex items-center justify-center gap-1.5 transition-colors shadow-2xs cursor-pointer"
                >
                  <Check className="w-3.5 h-3.5" />
                  <span>Verify Documents</span>
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* User Records Section */}
      <div>
        {/* Filter Tabs Bar with Numeric Badges */}
        <div className="flex items-center gap-2 overflow-x-auto pb-3 mb-4 border-b border-slate-100 scrollbar-none">
          <Button
            type="button"
            onClick={() => setActiveTab("booked")}
            className={`px-4 py-2 rounded-xl text-xs font-semibold flex items-center gap-2 transition-all cursor-pointer whitespace-nowrap ${activeTab === "booked"
              ? "bg-[#0f5940] text-white shadow-2xs"
              : "bg-slate-50 text-slate-600 hover:bg-slate-100"
              }`}
          >
            <Calendar className="w-3.5 h-3.5" />
            <span>Booked Rides</span>
          </Button>

          <Button
            type="button"
            onClick={() => setActiveTab("created")}
            className={`px-4 py-2 rounded-xl text-xs font-semibold flex items-center gap-2 transition-all cursor-pointer whitespace-nowrap ${activeTab === "created"
              ? "bg-[#0f5940] text-white shadow-2xs"
              : "bg-slate-50 text-slate-600 hover:bg-slate-100"
              }`}
          >
            <Compass className="w-3.5 h-3.5" />
            <span>Created Rides</span>
          </Button>

          <Button
            type="button"
            onClick={() => setActiveTab("vehicles")}
            className={`px-4 py-2 rounded-xl text-xs font-semibold flex items-center gap-2 transition-all cursor-pointer whitespace-nowrap ${activeTab === "vehicles"
              ? "bg-[#0f5940] text-white shadow-2xs"
              : "bg-slate-50 text-slate-600 hover:bg-slate-100"
              }`}
          >
            <Car className="w-3.5 h-3.5" />
            <span>User Vehicle</span>
          </Button>

          <Button
            type="button"
            onClick={() => setActiveTab("wallet")}
            className={`px-4 py-2 rounded-xl text-xs font-semibold flex items-center gap-2 transition-all cursor-pointer whitespace-nowrap ${activeTab === "wallet"
              ? "bg-[#0f5940] text-white shadow-2xs"
              : "bg-slate-50 text-slate-600 hover:bg-slate-100"
              }`}
          >
            <Receipt className="w-3.5 h-3.5" />
            <span>Wallet Transactions</span>
          </Button>

          <Button
            type="button"
            onClick={() => setActiveTab("referral")}
            className={`px-4 py-2 rounded-xl text-xs font-semibold flex items-center gap-2 transition-all cursor-pointer whitespace-nowrap ${activeTab === "referral"
              ? "bg-[#0f5940] text-white shadow-2xs"
              : "bg-slate-50 text-slate-600 hover:bg-slate-100"
              }`}
          >
            <Users className="w-3.5 h-3.5" />
            <span>Referral Users</span>
          </Button>
        </div>

        {/* Tab Content Display */}
        <div>
          {activeTab === "booked" && <RideBooked _id={match?.params.id} />}
          {activeTab === "created" && <RideCreated _id={match?.params.id} />}
          {activeTab === "vehicles" && <UserVehicles _id={match?.params.id} />}
          {activeTab === "wallet" && <WalletTransation _id={match?.params.id} />}
          {activeTab === "referral" && (
            <AgGridTable
              rowData={referralUsers || []}
              columnDefs={referralColumnDefs}
              loading={referralLoading}
              domLayout="autoHeight"
              emptyMessage="No referral users found"
            />
          )}
        </div>
      </div>

      {/* Document Full Image Modal Preview */}
      {previewDoc && (
        <div
          className="fixed inset-0 z-50 bg-black/60 backdrop-blur-xs flex items-center justify-center p-4 animate-in fade-in duration-150"
          onClick={() => setPreviewDoc(null)}
        >
          <div
            className="bg-white rounded-2xl max-w-2xl w-full p-4 overflow-hidden relative shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between pb-3 border-b border-slate-100">
              <span className="text-xs font-bold text-slate-800">Document Preview</span>
              <Button
                type="button"
                onClick={() => setPreviewDoc(null)}
                variant="ghost"
                size="icon"
                className="h-7 w-7 text-slate-400 hover:text-slate-800 rounded-lg cursor-pointer"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
            <div className="py-4 flex items-center justify-center max-h-[70vh] overflow-auto">
              <img
                src={henceforthApi.FILES.imageOriginal(previewDoc)}
                alt="Document Preview"
                className="max-w-full max-h-[60vh] object-contain rounded-lg"
              />
            </div>
          </div>
        </div>
      )}

      {/* Rejection Reason Modal */}
      <Dialog open={rejectModalOpen} onOpenChange={setRejectModalOpen}>
        <DialogContent className="sm:max-w-md p-6">
          <DialogHeader className="gap-1">
            <div className="w-10 h-10 rounded-xl bg-rose-50 text-rose-600 border border-rose-100 flex items-center justify-center mb-1">
              <AlertCircle className="w-5 h-5" />
            </div>
            <DialogTitle className="text-base font-bold text-slate-900">
              Reject {rejectingDocType}
            </DialogTitle>
            <DialogDescription className="text-xs text-slate-500">
              Please enter the reason for rejection so the user can understand what went wrong and re-upload correctly.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 py-1">
            {/* Quick preset suggestions */}
            <div>
              <label className="text-[11px] font-semibold text-slate-600 block mb-1.5">
                Quick reasons:
              </label>
              <div className="flex flex-wrap gap-1.5">
                {[
                  "Document has expired",
                  "Blurry or unreadable photo",
                  "Name mismatch with account",
                  "Wrong document uploaded",
                  "Cut-off / Incomplete document",
                ].map((reason) => (
                  <button
                    key={reason}
                    type="button"
                    onClick={() => setRejectionReason(reason)}
                    className={`text-[11px] px-2.5 py-1 rounded-full border transition-colors cursor-pointer ${rejectionReason === reason
                      ? "bg-rose-50 border-rose-300 text-rose-700 font-medium"
                      : "bg-slate-50 border-slate-200 text-slate-600 hover:bg-slate-100"
                      }`}
                  >
                    {reason}
                  </button>
                ))}
              </div>
            </div>

            {/* Rejection reason textarea */}
            <div>
              <label className="text-xs font-semibold text-slate-700 block mb-1">
                Rejection Reason <span className="text-rose-500">*</span>
              </label>
              <textarea
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                placeholder="Type the detailed reason for rejection here..."
                rows={3}
                className="w-full text-xs rounded-lg border border-slate-200 p-2.5 text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-rose-500/20 focus:border-rose-500 transition-colors resize-none"
              />
              <span className="text-[10px] text-slate-400">
                This reason will be provided to the user so they can take corrective action.
              </span>
            </div>
          </div>

          <DialogFooter className="gap-2 sm:gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setRejectModalOpen(false)}
              disabled={rejectSubmitting}
              className="text-xs cursor-pointer"
            >
              Cancel
            </Button>
            <Button
              type="button"
              size="sm"
              onClick={handleConfirmReject}
              disabled={!rejectionReason.trim() || rejectSubmitting}
              className="text-xs font-semibold bg-rose-600 hover:bg-rose-700 text-white gap-1.5 cursor-pointer"
            >
              {rejectSubmitting ? (
                <>
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  <span>Rejecting...</span>
                </>
              ) : (
                <>
                  <X className="w-3.5 h-3.5" />
                  <span>Confirm Rejection</span>
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};